/**
 * Grouping for the Audit page.
 *
 * Merchant Invoices answers "what does this merchant owe us" and School Pay
 * answers "what do we owe this school". Neither shows a transaction that fell
 * out of both: a sale at a merchant we do not bill, a sale with no school
 * attached, a refund. Audit is the row-level view underneath both, so it starts
 * from every transaction and annotates each one with where it reached in the
 * funnel rather than filtering any of them away.
 *
 * The month key and the money come from the same helpers the other two pages
 * use, so a figure here can never disagree with the page it came from.
 */

import {
  buildMerchantIndex,
  commissionFor,
  matchBillableMerchant,
  merchantNameFromTxn,
  monthKeyFromTxn,
  monthLabel,
} from './billing';
import { getSchoolRevenue, isNonBillableTxn } from './transactionRevenue';
// School Pay groups by the month the money was RECEIVED, not the month of the
// purchase, so a statement is keyed on that. Looking one up by the purchase
// month misses every transaction whose commission arrived in a later month.
import { monthKeyFromTxn as payoutMonthKey } from './schoolPay';
import { FUNNEL_STATE, currentMoney, fullMoney, funnelStatusFor } from './funnel';

export const merchantLabel = (t) =>
  t.olive_merchant_name || t.wildfire_merchant_name || merchantNameFromTxn(t);

/**
 * Which vendor reported it — the first thing you want when a row looks wrong.
 *
 * `ourMerchant` splits Olive in two, because the two halves mean different
 * things to us: a merchant we signed and added to Olive is one we invoice, and
 * one that was already in Olive's network is theirs to bill. Nothing in the
 * payload marks the difference — the HomecrowdMerchant list is the only record
 * of it, which is the same judgement Merchant Invoices makes.
 */
export const sourceOf = (t, ourMerchant = null) => {
  const id = String(t.external_txn_id || '');
  if (id.startsWith('wildfire_')) return 'Wildfire';
  if (id.startsWith('travel_') || id.startsWith('attraction_')) return 'Travel';
  if (id.startsWith('manual-')) return 'Manual';
  if (t.olive_merchant_id || t.olive_merchant_name) {
    return ourMerchant ? 'Olive (us)' : 'Olive';
  }
  return '—';
};

const schoolOf = (t) => {
  const s = t.school;
  if (!s) return null;
  if (typeof s === 'object') return { id: String(s.id), name: s.name || 'Unknown school' };
  return { id: String(s), name: 'Unknown school' };
};

/**
 * The fan, by name.
 *
 * The superuser feed serialises `user` as a bare id, so the name and email are
 * carried alongside it. Falls back through email to nothing rather than showing
 * a UUID, which identifies no one to a reader.
 */
const fanOf = (t) => {
  if (t.user_name) return t.user_name;
  if (t.user_email) return t.user_email;
  const u = t.user;
  if (u && typeof u === 'object') return u.email || u.username || null;
  return null;
};

/**
 * Every transaction, grouped by month.
 *
 * [{ key, label, txnCount, totalSales, totalCommission, totalShare, txns: [...] }]
 *
 * Each transaction carries what the two money pages would make of it:
 *   billable   — the merchant record Merchant Invoices would bill, or null
 *   commission — what that invoice would charge (0 when we do not bill it)
 *   share      — what School Pay would owe the school (0 when there is none)
 *   nonBillable— excluded from both because the sale did not stand
 */
export const groupAuditByMonth = (transactions, merchants, schoolsById = {}, sentIndex = null) => {
  const index = buildMerchantIndex(merchants);
  const byMonth = new Map();

  for (const t of transactions || []) {
    const mKey = monthKeyFromTxn(t);
    if (!mKey) continue;

    const nonBillable = isNonBillableTxn(t);
    // Whether the merchant is ours is a fact about the merchant, not about
    // whether this particular sale stood — so it is resolved first and the
    // billability gate applied after. A refunded sale at one of our merchants
    // is still a sale at one of our merchants.
    const ourMerchant = matchBillableMerchant(t, index);
    const billable = nonBillable ? null : ourMerchant;
    const school = schoolOf(t);
    // No school, no share. getSchoolRevenue falls back to a 70% split whether
    // or not a school is attached, so an unattributed commission would
    // otherwise contribute money to a "to schools" total that School Pay — which
    // skips schoolless transactions outright — never counts.
    const share = nonBillable || !school ? 0 : getSchoolRevenue(t);

    if (!byMonth.has(mKey)) {
      byMonth.set(mKey, {
        key: mKey,
        label: monthLabel(mKey),
        txnCount: 0,
        totalSales: 0,
        totalCommission: 0,
        totalShare: 0,
        txns: [],
      });
    }
    const month = byMonth.get(mKey);
    // The period School Pay would file this under, which is not always mKey.
    const payoutKey = payoutMonthKey(t);
    const statement =
      sentIndex && school ? sentIndex.lookup(school.id, payoutKey) : null;

    const funnel = funnelStatusFor(t, statement);
    // Audit normally shows where things stand now. On a reversal that would be
    // $0.00 for both figures — true, but it tells you nothing about what came
    // back. So a refunded or failed row shows the last non-zero value instead,
    // which is what the order was worth before it was undone. `fullMoney` falls
    // back to the current figure when there has never been a non-zero one, so a
    // commission that was always zero still reads $0.00.
    const undone = funnel === 'refunded' || funnel === 'failed';
    const { amount, commission: vendorCommission } = undone
      ? fullMoney(t)
      : currentMoney(t);
    // What the invoice would charge. Only merchants we signed are billed, so a
    // real Olive sale at someone else's merchant correctly contributes nothing.
    const commission = billable ? commissionFor(t, billable) : 0;

    month.txnCount += 1;
    month.totalSales += nonBillable ? 0 : amount;
    month.totalCommission += commission;
    month.totalShare += share;
    month.txns.push({
      id: t.id || t.external_txn_id,
      externalId: t.external_txn_id,
      date: t.auth_at || t.captured_at || t.created_at,
      source: sourceOf(t, ourMerchant),
      merchant: merchantLabel(t),
      fan: fanOf(t),
      fanEmail: t.user_email || null,
      schoolId: school ? school.id : null,
      schoolName: school ? (schoolsById[school.id]?.name || school.name) : null,
      status: t.status,
      vendorStatus: t.vendor_status || null,
      // Where the money actually is. Needs the statement for this school-month,
      // which is the only part of the state the transaction does not carry.
      funnel,
      payoutKey,
      statementSent: Boolean(statement && statement.is_sent),
      amount,
      // null, not 0: a hand-entered row never had a commission recorded, which
      // is a different fact from a vendor paying nothing on it.
      cashback:
        t.commission_amount == null &&
        t.current_commission == null &&
        t.full_commission == null
          ? null
          : vendorCommission,
      commission,
      share,
      points: Number(t.points_earned || 0),
      nonBillable,
      // Flagged rather than hidden here: Audit is where you go to find out why
      // a commission is worth nothing, so it has to be visible.
      everNonZero: t.ever_nonzero !== false,
      billableMerchantId: billable ? billable.id : null,
    });
  }

  for (const month of byMonth.values()) {
    month.txns.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  return Array.from(byMonth.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
};

/**
 * Everything one audit row says, as one lowercase string.
 *
 * Built from the columns as they READ rather than as they are stored, which is
 * the whole point: the funnel is searchable by its label ("received") as well
 * as its key, money by the figure the cell shows ("12.40", not "12.4"), and the
 * date by the words on screen ("aug 5") as well as its ISO form ("2026-08-05").
 * Otherwise a search matches what the record holds and not what the reader is
 * looking at. The external id and the fan's email are in here too though
 * neither is a visible column — both are things you arrive at this page
 * already holding, wanting the row that goes with them.
 */
const searchTextFor = (t) => {
  const when = t.date ? new Date(t.date) : null;
  const valid = when && !Number.isNaN(when.getTime());
  const money = (v) => (v == null ? '' : Number(v).toFixed(2));
  return [
    t.externalId,
    t.source,
    t.merchant,
    t.schoolName,
    t.fan,
    t.fanEmail,
    t.status,
    t.vendorStatus,
    t.funnel,
    FUNNEL_STATE[t.funnel] && FUNNEL_STATE[t.funnel].label,
    t.payoutKey,
    valid
      ? when.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '',
    valid ? when.toISOString().slice(0, 10) : '',
    money(t.amount),
    money(t.cashback),
    t.share ? money(t.share) : '',
    t.commission ? money(t.commission) : '',
    // The two marker columns carry no text of their own, so give the states
    // they represent something to match on.
    t.statementSent ? 'sent paid out' : 'unsent',
    t.nonBillable ? 'non-billable' : '',
    t.everNonZero ? '' : 'zero worthless',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

/**
 * Narrow one month's rows to those matching a free-text query.
 *
 * Every whitespace-separated term must match somewhere in the row, so adding a
 * word narrows the result rather than widening it — "olive refunded" is the two
 * together, not either. An empty query returns the rows untouched, so a section
 * that has never been searched pays nothing for the feature.
 */
export const filterAuditTxns = (txns, query) => {
  const terms = String(query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!terms.length) return txns || [];
  return (txns || []).filter((txn) => {
    const hay = searchTextFor(txn);
    return terms.every((term) => hay.includes(term));
  });
};
